/* eslint-disable indent */
import React from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { Field } from "formik";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Paper, Box, Icon } from "@material-ui/core";
import * as Yup from "yup";

import { TextInput, CheckboxInput, NumberInput } from "components/inputs";
import Form from "components/form";
import { showMessage } from "app/store/actions/message.actions";
import { getPatientIdentifierTypeOptions } from "app/auth/store/reducers/system-configuration";
import { saveExternalLinkTemplate } from "../actions/external-link-template.actions";

const linkName = "Link Name";
const url = "URL";
const displayOrder = "Display Order";

const validationSchema = Yup.object().shape({
  externalLinks: Yup.array().of(
    Yup.object().shape({
      linkName: Yup.string()
        .nullable()
        .max(18, "Link Name must be at most 18 characters")
        .test("linkName-required", function linkNameRequired(value) {
          const { path, createError, parent } = this;
          if (parent.isActive && (!value || value.trim() === "")) {
            const match = path.match(/\[(\d+)\]/);
            const linkNumber = match ? parseInt(match[1], 10) + 1 : "";
            return createError({
              path,
              message: `Link ${linkNumber} Name is required`,
            });
          }
          return true;
        }),
      linkUrl: Yup.string()
        .nullable()
        .max(2000, "URL must be at most 2000 characters")
        // Custom validation for URL - only validates when link is active.
        // Uses simple prefix check instead of Yup's .url() because URLs may contain
        // template placeholders like {PatientIdentifier:UMRN} which would fail strict URL validation.
      .test("linkUrl-required", function linkUrlRequired(value) {
          const { path, createError, parent } = this;

          if (!parent.isActive) {
            return true;
          }

          const match = path.match(/\[(\d+)\]/);
          const linkNumber = match ? parseInt(match[1], 10) + 1 : "";
          const fieldLabel = `Link ${linkNumber} URL`;
          const trimmedValue = value?.trim() ?? "";

          // Check required
          if (trimmedValue === "") {
            return createError({
              path,
              message: `${fieldLabel} is required`,
            });
          }

          // Validate URL starts with http:// or https://
          if (!trimmedValue.startsWith("http://") && !trimmedValue.startsWith("https://")) {
            return createError({
              path,
              message: `${fieldLabel} must start with http:// or https://`,
            });
          }

          return true;
        }),
      displayOrder: Yup.number()
        .nullable()
        .integer("Display Order must be a whole number")
        .min(1, "Display Order must be greater than or equal to 1"),
      isActive: Yup.boolean(),
    }),
  ),
});

const useStyles = makeStyles(theme => ({
  linkRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    "& > div": {
      minWidth: 0, // Allow flex items to shrink below content size
    },
    "& > div:nth-child(1)": { // Link Name
      flex: "1 0 120px",
      maxWidth: "200px",
    },
    "& > div:nth-child(2)": { // URL
      flex: "3 1 300px",
    },
    "& > div:nth-child(3)": { // Display Order
      flex: "0 0 140px",
      maxWidth: "140px",
    },
    "& > div:nth-child(4)": { // Active checkbox
      flex: "0 0 auto",
      marginTop: theme.spacing(2),
    },
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  helpSection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  helpHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  helpList: {
    marginTop: theme.spacing(1),
    paddingLeft: theme.spacing(3),
    "& li": {
      marginBottom: theme.spacing(0.5),
    },
  },
}));

const LinkTemplateGuidelinesSection = ({ classes }) => (
  <Paper elevation={1} className={classes.helpSection}>
    <Box className={classes.helpHeader}>
      <Icon color="primary" fontSize="small">info</Icon>
      <Typography variant="subtitle2" color="textPrimary">
        External Link Guidelines
      </Typography>
    </Box>
    <ul className={classes.helpList}>
      <li>
        <Typography variant="body2" color="textSecondary">
          When active, external links are displayed (as buttons) on various headers where patient details are shown. Clicking will open the configured URL in a new browser tab.
        </Typography>
      </li>
      <li>
        <Typography variant="body2" color="textSecondary">
          Inactive external links will be hidden for most users but are still visible to Administrators, allowing for testing prior to activation.
        </Typography>
      </li>
    </ul>
  </Paper>
);

const PatientIdentifierHelpSection = ({ patientIdentifierTypes, classes }) => {
  if (!patientIdentifierTypes?.length) {
    return null;
  }

  return (
    <Paper elevation={1} className={classes.helpSection}>
      <Box className={classes.helpHeader}>
        <Icon color="primary" fontSize="small">info</Icon>
        <Typography variant="subtitle2" color="textPrimary">
          Patient Identifier Placeholders
        </Typography>
      </Box>
      <Typography variant="body2" color="textSecondary">
        Patient identifier placeholders may be included in external link URLs with the format <code>{"{patientIdentifier:Code}"}</code>
      </Typography>
      <Typography variant="body2" color="textSecondary">
        Each placeholder will be replaced with the corresponding patient identifier value (if present) when the link is displayed. Below are the available patient identifier types that may be used:
      </Typography>
      <ul className={classes.helpList}>
        {patientIdentifierTypes.map(type => (
          <li key={type.value}>
            <Typography variant="body2" color="textSecondary">
              <strong>{type.value}</strong> ({type.label}) - Use: <code>{`{patientIdentifier:${type.value}}`}</code>
            </Typography>
          </li>
        ))}
      </ul>
    </Paper>
  );
};

const ExternalLinkTemplateForm = ({
  externalLinks = [],
  showMessage: displayMessage,
  disabled = true,
  onCancel,
  onSucceed,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const patientIdentifierTypes = useSelector(getPatientIdentifierTypeOptions);

  const initialValues = {
    externalLinks,
  };

  const handleSubmit = (values, { setSubmitting, setErrors }) => {
    const { externalLinks: externalLinksToSave } = values;

    dispatch(saveExternalLinkTemplate(externalLinksToSave)).then(response => {
      setSubmitting(false);

      if (response.error !== true) {
        displayMessage({
          variant: "success",
          message: "External links saved successfully",
        });
        onSucceed?.();
      } else {
        setErrors(response.payload);
      }
    });
  };

  return (
    <Form
      disabled={disabled}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      isModal={false}
      content={() => (
        <>
          {externalLinks.length === 0 ? (
            <Typography className={classes.emptyState}>
              No external links configured
            </Typography>
          ) : (
            externalLinks.map((link, index) => (
              <div key={link.id || index} className={classes.linkRow}>
                <Field
                  name={`externalLinks[${index}].linkName`}
                  label={`Link ${index + 1} ${linkName}`}
                  component={TextInput}
                  placeholder="Link Name"
                />
                <Field
                  name={`externalLinks[${index}].linkUrl`}
                  label={`Link ${index + 1} ${url}`}
                  component={TextInput}
                  placeholder="URL"
                />
                <Field
                  name={`externalLinks[${index}].displayOrder`}
                  label={displayOrder}
                  component={NumberInput}
                  placeholder="Display Order"
                />
                <Field
                  name={`externalLinks[${index}].isActive`}
                  component={CheckboxInput}
                  label="Active"
                />
              </div>
            ))
          )}
          <LinkTemplateGuidelinesSection classes={classes} />
          <PatientIdentifierHelpSection
            patientIdentifierTypes={patientIdentifierTypes}
            classes={classes}
          />
        </>
      )}
    />
  );
};

export default connect(
  null,
  { showMessage },
)(ExternalLinkTemplateForm);
